import { defineComponent, computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import {
  NavBar as VanNavBar,
  Icon as VanIcon,
  Row as VanRow,
  Col as VanCol,
  List as VanList,
  PullRefresh as VanPullRefresh,
  showToast,
} from 'vant'
import { getApiV1CommunityList, getApiV1CommunityForumPublishCheck } from '@/api/sdk.gen'
import { useAuthStore } from '@/stores/auth-store'
import emptyImg from '@/assets/empty-data.png'
import iconPublish from '@/assets/illustrations/publish.png'

// community/list 返回的图解贴单条结构（仅取本页用到的字段）
interface IllustrationPost {
  id: number
  title?: string
  content?: string
  publish_time?: number
  created_at?: string
  author?: { id?: number; nickname?: string; avatar?: string } | null
  period?: string
  lottery_type?: number
}

function formatTime(p: IllustrationPost): string {
  let t = 0
  if (p.publish_time) t = p.publish_time * 1000
  else if (p.created_at) t = new Date(p.created_at).getTime()
  if (!t) return ''
  const d = new Date(t)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default defineComponent({
  name: 'GalleryIllustrationsPage',
  setup() {
    const router = useRouter()
  const goBack = useSmartBack()
    const route = useRoute()
    const authStore = useAuthStore()

    const tukuName = computed(() => String(route.query.tukuName ?? ''))
    const year = computed(() => Number(route.query.year) || new Date().getFullYear())
    const period = computed(() => Number(route.query.period) || 0)
    const lotteryType = computed<1 | 2>(() => (route.query.lotteryType === '1' ? 1 : 2))

    // 标题精确前缀：图解贴标题由前端 forum-publish 自动生成为 第YYYY{PPP}期{图库名}图解
    const titlePrefix = computed(
      () => `第${year.value}${String(period.value).padStart(3, '0')}期`,
    )

    const posts = ref<IllustrationPost[]>([])
    const loading = ref(false)
    const refreshing = ref(false)
    const finished = ref(false)
    const error = ref(false)
    const page = ref(1)
    const SIZE = 20

    async function loadPage(reset = false) {
      if (loading.value && !reset) return
      loading.value = true
      error.value = false
      try {
        if (reset) {
          page.value = 1
          finished.value = false
          posts.value = []
        }
        const res = await getApiV1CommunityList({
          query: {
            page: page.value,
            size: SIZE,
            category: 2,
            lottery_type: lotteryType.value,
            // 后端 keyword 是 LIKE %x%；用图库名宽搜，前端再用标题前缀精确过滤
            keyword: tukuName.value ? `${tukuName.value}图解` : undefined,
          },
        })
        const list = (res.data?.data as { list?: IllustrationPost[] } | undefined)?.list ?? []
        const filtered =
          period.value > 0
            ? list.filter((p) => {
                const t = String(p.title || '')
                return (
                  t.startsWith(titlePrefix.value) &&
                  (!tukuName.value || t.includes(tukuName.value))
                )
              })
            : list
        posts.value = reset ? filtered : [...posts.value, ...filtered]
        if (list.length < SIZE) finished.value = true
        else page.value++
      } catch {
        error.value = true
      } finally {
        loading.value = false
        refreshing.value = false
      }
    }

    onMounted(() => loadPage(true))

    async function onGoPublish() {
      if (!authStore.isLoggedIn) {
        router.push('/login')
        return
      }
      try {
        const res = await getApiV1CommunityForumPublishCheck()
        const check = res.data?.data
        if (check?.allow === false) {
          showToast(check.msg || '注册满24小时后才能发帖')
          return
        }
      } catch {
        showToast('操作失败')
        return
      }
      router.push({
        path: '/forum/publish',
        query: {
          source: 'illustration',
          tukuId: String(route.params.tukuId ?? ''),
          year: String(year.value),
          period: String(period.value),
          tukuName: tukuName.value,
          lotteryType: String(lotteryType.value),
        },
      })
    }

    return () => (
      <div class="picture_comments">
        <VanNavBar
          fixed
          title={tukuName.value || '图解'}
          onClickLeft={() => goBack()}
        >
          {{
            left: () => <VanIcon name="arrow-left" color="#333" size="16" />,
            right: () => (
              <button
                type="button"
                class="nav-icon-text btn-reset"
                style="margin-right:8px"
                onClick={onGoPublish}
              >
                <span class="icon block">
                  <img src={iconPublish} alt="" />
                </span>
                <span class="text block">发布图解</span>
              </button>
            ),
          }}
        </VanNavBar>

        <div class="list-bd">
          <VanPullRefresh
            modelValue={refreshing.value}
            onUpdate:modelValue={(v: boolean) => (refreshing.value = v)}
            onRefresh={() => loadPage(true)}
          >
            <VanList
              loading={loading.value}
              onUpdate:loading={(v: boolean) => (loading.value = v)}
              error={error.value}
              onUpdate:error={(v: boolean) => (error.value = v)}
              finished={finished.value}
              finishedText=""
              errorText="加载失败，点击重试"
              immediateCheck={false}
              onLoad={() => loadPage(false)}
            >
              {posts.value.length === 0 && !loading.value && finished.value ? (
                <div class="empty-box">
                  <img src={emptyImg} alt="" />
                  <p>暂无数据</p>
                </div>
              ) : (
                <ul class="forum-list type12">
                  {posts.value.map((p) => (
                    <li key={p.id}>
                      <VanRow>
                        <VanCol span={17}>
                          <div class="content">
                            <div>
                              <span class="csize">{p.title}</span>
                            </div>
                            <div class="bsize">
                              {/* 正文是 innerHTML，链接包在外面 */}
                              <RouterLink class="btn-reset block" to={`/forum/detail/${p.id}`}>
                                <div class="text van-multi-ellipsis--l3" innerHTML={p.content ?? ''} />
                              </RouterLink>
                            </div>
                            <div class="footer">
                              {p.author?.avatar && <img src={p.author.avatar} alt="" />}
                              <span class="span">{p.author?.nickname || '匿名'}</span>
                              <span>{formatTime(p)}</span>
                            </div>
                          </div>
                        </VanCol>
                        <VanCol span={5} />
                      </VanRow>
                    </li>
                  ))}
                </ul>
              )}
            </VanList>
          </VanPullRefresh>
        </div>
      </div>
    )
  },
})
